import {Component, HostListener, OnInit} from '@angular/core';
import {Sidenavaction} from 'src/assets/js/application/sidenavaction';
import {FarmSiteService} from 'src/app/services/farmSite.service';
import FarmSiteModel from 'src/app/services/farmSite.model';
import {ReadingsService} from 'src/app/services/readings.service';
import ReadingsModel from 'src/app/services/readings.model';
import * as moment from 'moment';
import * as _ from 'lodash';
import {CommonMethodsService} from 'src/app/services/commonMethods.service';
import {LocationStrategy} from '@angular/common';
import {PreviousRouteService} from 'src/app/services/previousRouteService';
import {BatchLoader} from "../../../../Utils/batch-loader.util";

/**
 * @component NewWaterQualityComponent
 * @selector app-new-water-quality
 * @implements {OnInit}
 *
 * @description
 * The `NewWaterQualityComponent` is responsible for displaying a list of recent water quality readings
 * across the farm business. It serves as a dashboard widget or view to quickly monitor the latest
 * water parameters from various ponds.
 *
 * ## Key Responsibilities:
 * 1. **Data Aggregation**: Fetches recent water quality readings from all farm sites associated with the user's business.
 * 2. **Filtering & Search**: Provides client-side filtering capabilities (though search implementation details might be limited in this snippet).
 * 3. **Sorting**: Allows users to sort the readings list by Farm Name, Section Name, Pond Name, or Location Name.
 * 4. **Pagination/Infinite Scroll**: Implements a batch loading mechanism to handle large datasets efficiently.
 * 5. **Detail View**: Allows users to view detailed readings for a specific pond, including custom parameters.
 * 6. **Role-Based Access**: Adapts data retrieval based on the logged-in user's role (Farmer, Farm Manager, Business Admin, Provider).
 *
 * ## User Roles & Permissions:
 * - **Farm Category Users**:
 *   - **Business Admin / Farmer**: Access to readings from all farm sites in their business.
 *   - **Farm Manager**: Restricted access to readings from farm sites assigned to them.
 * - **Provider Category Users**:
 *   - **Business Admin**: Access to readings from farm sites associated with the provider business.
 * - **Other Categories**:
 *   - **Employee**: Access to all farm sites (typically for internal admin/support).
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role.
 * - `loginBusinessCategory`: Business context.
 * - `businessID`: ID of the logged-in user's business.
 * - `personID`: ID of the logged-in user.
 * - `loginBusinessName`: Used for display.
 *
 * ## Data Flow:
 * 1. **Initialization**: `ngOnInit` determines user context and fetches relevant farm sites.
 * 2. **Data Fetching**: `readingsService.getReadingsByFarmSite` is called with the list of farm site IDs.
 * 3. **Display**: Data is loaded into `readings` (display list) via `BatchLoader`.
 * 4. **Detail View**: `getAllTodayReadings` fetches detailed readings for a selected pond.
 *
 * ## UI State Management:
 * - **Loading**: `pgLoad`, `listLoader` manage loading indicators.
 * - **Sorting**: Flags like `pondfarmAsc`, `pondnameAsc` manage sort direction.
 * - **Pagination**: `BatchLoader` handles infinite scroll pagination.
 */
@Component({
  selector: 'app-new-water-quality',
  templateUrl: './new-water-quality.component.html',
  styleUrls: ['./new-water-quality.component.scss']
})
export class NewWaterQualityComponent implements OnInit {
  /** @type {Array<FarmSiteModel>} List of farm sites associated with the user. */
  public farmsiteEntities: Array<FarmSiteModel>;

  /** @type {Array<ReadingsModel>} The list of readings currently displayed in the UI. */
  public readings: Array<ReadingsModel>;

  /** @type {Array<ReadingsModel>} List of readings for a specific pond (detail view). */
  public readingsByPondId: Array<ReadingsModel>;

  /** @type {string} Search keyword. */
  public search: string;

  /** @type {boolean} Flag to hide business details block (unused in current logic). */
  public hideBusinessDetailsBlock: boolean;

  /** @type {string} Role of the logged-in user. */
  public loginPersonType: string;

  /** @type {string} Name of the logged-in user's business. */
  public loginUserBusinessName: string;

  /** @type {string} Category of the logged-in user's business. */
  public loginUserBusinessCategory: string;

  // pagination
  /** @type {number} Current page number. Default 1. */
  public currentPageNumber: number = 1;

  /** @type {number} Items per page. Default 15. */
  public noOfItemsPerPage: number = 15;

  /** @type {string} Name of the selected pond for display. */
  public dispPondName: string;

  /** @type {boolean} Flag for page loading state. */
  public pgLoad = false;

  /** @type {boolean} Flag for sorting by Section Name. */
  public pondsectionnameAsc: boolean;

  /** @type {boolean} Flag for sorting by Location Name. */
  public pondlocationnameAsc: boolean;

  /** @type {boolean} Flag for sorting by Pond Name. */
  public pondnameAsc: boolean;

  /** @type {Array<any>} Original list of readings before filtering. */
  public originalnewTestsList = new Array();

  /** @type {boolean} Flag for sorting by Farm Name. */
  public pondfarmAsc: boolean;

  /** @type {any[]} Array of processed readings for display (detail view). */
  public resultantReadings: any[];

  /** @type {boolean} Flag controlling list loader spinner. */
  public listLoader = false;              // Show listLoader spinner

  /** @type {number} Start index for batch loading. */
  public startIndex = 0;             // Tracks how much has been loaded

  /** @type {boolean} Flag indicating if filtering is active. */
  public isFiltering: boolean = false;

  /** @type {boolean} Flag indicating if list is empty. */
  public noList: boolean = false;

  /** @type {boolean} Flag to enable/disable page loader (unused). */
  public enableDisablePageLoader: boolean = false;

  /** @type {any} Timeout for search debounce (unused). */
  searchTimeout: any;

  /** @type {BatchLoader<any>} Batch loader utility instance. */
  private batchLoader: BatchLoader<any>;

  /**
   * Constructor for NewWaterQualityComponent.
   * Initializes services.
   *
   * @param {FarmSiteService} farmsiteEntityService - Service for farm site operations.
   * @param {ReadingsService} readingsService - Service for readings operations.
   * @param {Sidenavaction} sidenavaction - Service for side navigation.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking route history.
   */
  constructor(public farmsiteEntityService: FarmSiteService, public readingsService: ReadingsService, public sidenavaction: Sidenavaction, public commonMethodsService: CommonMethodsService,
              private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Retrieves user context from `localStorage`.
   * - Sets up the previous route.
   * - Determines logic flow based on `loginBusinessCategory` and `loginPersonType`.
   * - Fetches farm sites and then readings associated with those sites.
   * - Initializes the batch loader for pagination.
   *
   * @throws {Error} Captures and displays errors via `commonMethodsService` if initialization fails.
   */
  public async ngOnInit() {
    try {
      window.scrollTo(0, 0);
      this.isFiltering = false;
      this.readings = [];
      this.originalnewTestsList = [];
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      this.hideBusinessDetailsBlock = false;
      // loginperson type details
      this.loginPersonType = localStorage.getItem('loginPersonType');
      // login person Business name
      this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
      this.loginUserBusinessCategory = localStorage.getItem('loginBusinessCategory');
      let farmsiteIds = new Array();
      // get person list based on login businessid
      if ((this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') && this.loginUserBusinessCategory == 'Farm') {
        let farmSitesByBusinessId = await this.farmsiteEntityService.getFarmeSitesByFarmId(localStorage.businessID);
        farmsiteIds = farmSitesByBusinessId.map(farmsite => farmsite.id);
        this.readings = await this.readingsService.getReadingsByFarmSite(farmsiteIds);
        this.originalnewTestsList = Object.assign({}, this.readings);
        this.readings = _.orderBy(this.readings, ['date', 'testing_time'], ['asc', 'desc']);
        this.pgLoad = true;
      } else if (this.loginPersonType == 'Business Admin' && this.loginUserBusinessCategory == 'Provider') {
        let farmSitesByBusinessId = await this.farmsiteEntityService.getFarmeSitesBusinessDetailsByProviderId(localStorage.businessID);
        farmsiteIds = farmSitesByBusinessId.map(farmsite => farmsite.id);
        this.readings = await this.readingsService.getReadingsByFarmSite(farmsiteIds);
        this.originalnewTestsList = Object.assign({}, this.readings);
        this.readings = _.orderBy(this.readings, ['date', 'testing_time'], ['asc', 'desc']);
        this.pgLoad = true;
      } else {
        let farmSitesByBusinessId = await this.farmsiteEntityService.getAllFarmSites();
        farmsiteIds = farmSitesByBusinessId.map(farmsite => farmsite.id);
        this.readings = await this.readingsService.getReadingsByFarmSite(farmsiteIds);
        this.originalnewTestsList = Object.assign({}, this.readings);
        this.readings = _.orderBy(this.readings, ['date', 'testing_time'], ['asc', 'desc']);
        this.pgLoad = true;
      }
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader?.reset();     // Clear state
      await this.batchLoader?.loadNextBatch(true); // Load first batch
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method onScroll
   * @description Host listener for window scroll events. Triggers `checkScrollAndLoad` to handle infinite scrolling.
   * @lifecycle HostListener
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  private scrollTimeout: any;

  /**
   * @method checkScrollAndLoad
   * @private
   * @description Debounced handler for scroll events. Checks if the user has scrolled near the bottom of the page.
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * @method initializeBatchLoader
   * @private
   * @description Initializes the `BatchLoader` utility with the master data list and callback functions for UI updates.
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.readings : this.originalnewTestsList,
      (updatedList) => {
        this.readings = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * @method getAllTodayReadings
   * @async
   * @description Fetches detailed readings for a specific pond.
   * Processes the readings, including custom parameters, for display in a modal or detail view.
   * @param {number} tankId - The ID of the pond (tank).
   * @side-effect Updates `readingsByPondId`, `resultantReadings`, `dispPondName`.
   */
  public async getAllTodayReadings(tankId: number) {
    try {
      this.pgLoad = false;
      if (tankId !== -1) {
        this.readingsByPondId = await this.readingsService.getReadingsByPondId(tankId);
        this.resultantReadings = new Array();
        if (this.readings !== undefined) {
          for (let i = 0; i < this.readingsByPondId.length; i++) {
            if (this.readingsByPondId[i].time) {
              this.readingsByPondId[i].time = moment(this.readingsByPondId[i].time).utc().format('DD-MMM-YYYY h:mm:ss a');
            }
            // end reading time
          }
          if (this.readingsByPondId != undefined && this.readingsByPondId.length > 0) {
            this.dispPondName = this.readingsByPondId[0].pond.name;
          }
          for (let i = 0; i < this.readingsByPondId.length; i++) {
            let eachObj = this.readingsByPondId[i];
            if (eachObj.editedReadings) {
              let editedReading: any;
              editedReading = this.readingsByPondId[i].editedReadings;
              editedReading.pond = this.readingsByPondId[i].pond;
              editedReading.pondCulture = this.readingsByPondId[i].pondCulture;
              editedReading.device = this.readingsByPondId[i].device;
              editedReading.farm_name = this.readingsByPondId[i].farm_name;
              this.resultantReadings.push(editedReading);
            } else {
              this.resultantReadings.push(this.readingsByPondId[i]);
            }
          }
          for (let i = 0; i < this.resultantReadings.length; i++) {
            if (this.resultantReadings[i].cust_added_params != undefined && this.resultantReadings[i].cust_added_params.length > 0 && this.resultantReadings[i].cust_added_params != null) {
              let adpVal = this.resultantReadings[i].cust_added_params;
              adpVal = JSON.parse(adpVal);
              this.resultantReadings[i].cust_added_params = adpVal;
            }
          }
          this.pgLoad = true;
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method chemicalParams
   * @description Checks if chemical parameters (alkalinity, carbonate, etc.) are missing or null.
   * Used to determine if certain sections should be displayed.
   * @param {ReadingsModel} reading - The reading object to check.
   * @returns {boolean} True if all checked chemical parameters are missing/null.
   */
  public chemicalParams(reading: ReadingsModel) {
    try {
      let params = (reading.alkalinity === -1 || reading.alkalinity === null) && (reading.carbonate === -1 || reading.carbonate === null) && (reading.ta === 0 || reading.ta === -1 || reading.ta === null) && (reading.uam === -1 || reading.uam === null) && (reading.tan === -1 || reading.tan === null) && (reading.bicarbonate === -1 || reading.bicarbonate === -1) && (reading.nitrite === -1 || reading.nitrite === null) && (reading.calcium === -1 || reading.calcium === null) && (reading.magnesium === -1 || reading.magnesium === null) && (reading.hardness === -1 || reading.hardness === null);
      return params;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getAddiParamOriginalVal
   * @description Removes '%20' (URL encoded space) from a string value.
   * @param {string} val - The string to process.
   * @returns {string} The processed string.
   */
  public getAddiParamOriginalVal(val: string) {
    try {
      return val.replace(/%20/g, '');
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method sortingReadings
   * @description Sorts the displayed readings list based on the specified type.
   * Supports sorting by 'farm', 'sectionname', 'pondname', 'locationname'.
   * @param {string} type - The field to sort by.
   */
  public sortingReadings(type: string) {
    try {
      if (type == 'farm') {
        this.pondfarmAsc = !this.pondfarmAsc;
        let orderbyval = 'desc';
        if (this.pondfarmAsc == true) {
          orderbyval = 'asc';
        }
        this.readings = _.orderBy(this.originalnewTestsList, ['FarmSiteDetails[0].businessID.businessName'], [orderbyval]);
        this.pondsectionnameAsc = false;
        this.pondlocationnameAsc = false;
        this.pondnameAsc = false;
      }
      if (type == 'sectionname') {
        this.pondsectionnameAsc = !this.pondsectionnameAsc;
        let orderbyval = 'desc';
        if (this.pondsectionnameAsc == true) {
          orderbyval = 'asc';
        }
        this.readings = _.orderBy(this.originalnewTestsList, ['section_name'], [orderbyval]);
        this.pondnameAsc = false;
        this.pondfarmAsc = false;
        this.pondlocationnameAsc = false;
      }
      if (type == 'pondname') {
        this.pondnameAsc = !this.pondnameAsc;
        let orderbyval = 'desc';
        if (this.pondnameAsc == true) {
          orderbyval = 'asc';
        }
        this.readings = _.orderBy(this.originalnewTestsList, ['name'], [orderbyval]);
        this.pondfarmAsc = false;
        this.pondlocationnameAsc = false;
        this.pondsectionnameAsc = false;
      }
      if (type == 'locationname') {
        this.pondlocationnameAsc = !this.pondlocationnameAsc;
        let orderbyval = 'desc';
        if (this.pondlocationnameAsc == true) {
          orderbyval = 'asc';
        }
        this.readings = _.orderBy(this.originalnewTestsList, ['FarmSiteDetails[0].address.village'], [orderbyval]);
        this.pondfarmAsc = false;
        this.pondnameAsc = false;
        this.pondsectionnameAsc = false;
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }
}
